/**
 * FormFieldRenderer.jsx
 * 
 * Main form field renderer - handles layout and delegates to field components.
 * This component replicates the EXACT layout structure from App.js:
 * - 12-column grid system
 * - Row grouping based on field widths
 * - Section support
 * - Conditional field visibility
 * 
 * STABILITY SAFEGUARDS:
 * 1. Integrates with FormReadyContext for "Wait for Data" promise
 * 2. Supports schema versioning via schemaNormalizer
 * 3. Works with print-document.css for isolated print styles
 * 
 * IMPORTANT: This file should ONLY handle layout. Individual field rendering
 * is delegated to components in ./fields/
 */

import React, { useEffect, useMemo } from 'react';
import { FormFieldProvider, useFormFieldContext } from './FormFieldContext';
import { useFormReady } from './FormReadyContext';
import { normalizeNoteData, mergeWithTemplate } from '../../utils/schemaNormalizer';
import { 
  migrateFormFieldsToRowGroups, 
  getColumnSpan, 
  getFieldStyle,
  shouldShowConditionalField 
} from './utils/rowGroupUtils';

// Import field components
import { renderBasicField } from './fields/basic';
import { renderLayoutField } from './fields/layout';
import { renderSpecialtyField } from './fields/specialty';

/**
 * Render a single field - delegates to appropriate field component
 * This mirrors the structure of renderField in App.js
 */
const renderField = (field, index, allFields, context) => {
  const { newNoteFormData } = context;
  
  // Get value helper for conditional logic
  const getFieldValue = (fieldId) => newNoteFormData[fieldId] || '';
  
  // Check conditional visibility
  if (!shouldShowConditionalField(field, allFields, getFieldValue)) {
    return null;
  }
  
  // Get field wrapper style
  const fieldStyle = getFieldStyle(field);
  
  // Try layout fields first (heading, divider, spacer, section_header, static_text)
  const layoutResult = renderLayoutField(field, index, fieldStyle, context);
  if (layoutResult !== null) {
    return layoutResult;
  }
  
  // Try specialty fields (client_*, treatment_plan_goals, consent_affirmation, etc.)
  // allFields lets the medications field bridge rows into a medication_plan field.
  const specialtyResult = renderSpecialtyField(field, index, fieldStyle, { ...context, allFields });
  if (specialtyResult !== null) {
    return specialtyResult;
  }
  
  // Default to basic fields (text, textarea, select, checkbox, radio, etc.)
  return renderBasicField(field, index, fieldStyle, context);
};

/**
 * FormFieldRendererInner - the actual renderer (must be inside FormFieldProvider)
 */
const FormFieldRendererInner = ({ 
  fields = [], 
  sections = null,
  onChange,
  disabled = false,
  mode = 'edit', // 'edit' for interactive inputs, 'view' for styled read-only display
  rawNoteContent = null, // Raw note content for backward compatibility normalization
  templateData = null // Template data for schema merging
}) => {
  const context = useFormFieldContext();
  const { registerPendingField, markFieldReady, isReady } = useFormReady();
  
  // Normalize legacy data if raw content provided
  const normalizedData = useMemo(() => {
    if (!rawNoteContent) return null;
    const normalized = normalizeNoteData(rawNoteContent, templateData);
    return mergeWithTemplate(normalized, templateData);
  }, [rawNoteContent, templateData]);
  
  // Signal that we're rendering fields
  useEffect(() => {
    const fieldCount = sections 
      ? sections.reduce((sum, s) => sum + (s.fields?.length || 0), 0)
      : fields.length;
    
    if (fieldCount > 0) {
      registerPendingField('form-renderer');
    }
  }, [fields.length, sections, registerPendingField]);
  
  // Signal ready when all fields are rendered
  useEffect(() => {
    // Small delay to ensure DOM is painted
    const timer = setTimeout(() => {
      markFieldReady('form-renderer');
    }, 50);
    return () => clearTimeout(timer);
  }, [markFieldReady]);
  
  // Merge normalized data into context if available
  const mergedFormData = normalizedData?.form_data || context.newNoteFormData;
  
  // Merge onChange prop into context if provided
  // mode from props takes precedence, then context, then default 'edit'
  const renderContext = {
    ...context,
    newNoteFormData: mergedFormData,
    disabled: mode === 'view' ? true : disabled,
    mode: mode || context.mode || 'edit',
    handleFieldChange: onChange || context.setNewNoteFormData,
  };
  
  // Check if we have sections structure
  const hasSections = sections && Array.isArray(sections) && sections.length > 0;
  const sectionsData = hasSections ? sections : null;
  const fieldsToRender = hasSections ? null : fields;
  
  // Main rendering - handle both structures (matches App.js exactly)
  // Added overflow protection to prevent horizontal page bleeding
  if (hasSections) {
    // NEW structure with sections - use row groups
    // Flattened list so cross-section lookups (conditional parents,
    // medication_plan bridge) see every field on the template.
    const flatAllFields = sectionsData.flatMap(s => s.fields || []);
    return (
      <div className="space-y-6" style={{ overflowWrap: 'break-word', maxWidth: '100%' }}>
        {sectionsData.map((section, sectionIndex) => {
          // Convert section fields to row groups
          const rowGroups = migrateFormFieldsToRowGroups(section.fields || []);
          const allSectionFields = flatAllFields;
          
          return (
            <div key={sectionIndex} className="space-y-3">
              {section.name && (
                <h4 className="text-md font-semibold text-gray-800 border-b pb-1">
                  {section.name}
                </h4>
              )}
              <div className="space-y-3">
                {rowGroups.map((row, rowIndex) => {
                  // Filter out row_placeholder fields
                  const visibleFields = row.fields.filter(f => f.type !== 'row_placeholder');
                  if (visibleFields.length === 0) return null;
                  
                  return (
                    <div 
                      key={`section-${sectionIndex}-row-${rowIndex}`} 
                      className="grid grid-cols-12 gap-3"
                      style={{ overflowWrap: 'break-word', maxWidth: '100%' }}
                    >
                      {visibleFields.map((field, fieldIndex) => {
                        const colSpan = getColumnSpan(field.width);
                        
                        return (
                          <div 
                            key={field.id || `field-${sectionIndex}-${fieldIndex}`}
                            style={{ 
                              gridColumn: `span ${colSpan} / span ${colSpan}`,
                              overflowWrap: 'break-word',
                              minWidth: 0 // Prevents grid blowout
                            }}
                          >
                            {renderField(field, `${sectionIndex}-${fieldIndex}`, allSectionFields, renderContext)}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  } else {
    // OLD structure (flat fields) - convert to row groups
    const rowGroups = migrateFormFieldsToRowGroups(fieldsToRender || []);
    const allFields = fieldsToRender || [];
    
    return (
      <div className="space-y-3" style={{ overflowWrap: 'break-word', maxWidth: '100%' }}>
        {rowGroups.map((row, rowIndex) => {
          // Filter out row_placeholder fields
          const visibleFields = row.fields.filter(f => f.type !== 'row_placeholder');
          if (visibleFields.length === 0) return null;
          
          return (
            <div 
              key={`row-${rowIndex}`} 
              className="grid grid-cols-12 gap-2"
              style={{ overflowWrap: 'break-word', maxWidth: '100%' }}
            >
              {visibleFields.map((field, fieldIndex) => {
                const colSpan = getColumnSpan(field.width);
                
                return (
                  <div 
                    key={field.id || `field-${fieldIndex}`}
                    style={{ 
                      gridColumn: `span ${colSpan} / span ${colSpan}`,
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      minWidth: 0 // Prevents grid blowout
                    }}
                  >
                    {renderField(field, fieldIndex, allFields, renderContext)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
};

/**
 * Main FormFieldRenderer component with Context wrapper.
 *
 * Plan C perf: wrapped in React.memo so that App-level re-renders that
 * don't change this component's specific props no longer cascade into
 * a full re-render of the form field tree. This relies on stable
 * function identities for the bridge functions inside `contextValue`,
 * which Plan B established by lifting them out of inline JSX and
 * wrapping in useCallback at the App body level.
 *
 * Custom comparator: shallow-compare top-level props. We treat the
 * `contextValue` object specially — shallow-compare its keys so a
 * caller passing `{...spread}` per render doesn't defeat memoization
 * unless something inside it actually changed.
 */
const FormFieldRenderer = React.memo(({
  fields = [],
  sections = null,
  contextValue = {},
  onChange,
  disabled = false,
  mode = 'edit', // 'edit' for interactive inputs, 'view' for styled read-only display
  rawNoteContent = null, // For backward compatibility with legacy notes
  templateData = null // Template data for schema merging
}) => {
  return (
    <FormFieldProvider value={contextValue}>
      <FormFieldRendererInner
        fields={fields}
        sections={sections}
        onChange={onChange}
        disabled={disabled}
        mode={mode}
        rawNoteContent={rawNoteContent}
        templateData={templateData}
      />
    </FormFieldProvider>
  );
}, (prev, next) => {
  // Top-level shallow check
  if (prev.fields !== next.fields) return false;
  if (prev.sections !== next.sections) return false;
  if (prev.onChange !== next.onChange) return false;
  if (prev.disabled !== next.disabled) return false;
  if (prev.mode !== next.mode) return false;
  if (prev.rawNoteContent !== next.rawNoteContent) return false;
  if (prev.templateData !== next.templateData) return false;
  // contextValue is rebuilt inline at the call site every App render
  // (it's a literal object), so reference-equal will almost never be
  // true. Shallow-compare its keys instead — the bridges
  // (handleFieldChange / setNewNoteFormData) are now memoized at App
  // body level (Plan B), so as long as nothing else in contextValue
  // changed we can safely skip the re-render.
  const a = prev.contextValue || {};
  const b = next.contextValue || {};
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
});

FormFieldRenderer.displayName = 'FormFieldRenderer';

export default FormFieldRenderer;
export { FormFieldRenderer, FormFieldRendererInner, renderField };
